import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { TextField, Button, Checkbox, Select, MenuItem, Autocomplete } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import { useEffect, useState } from "react";
import countries from "../../helpers/constants/countries.json";
import { useAddRoleMutation, useLazyGetRoleByIdQuery, useUpdateRoleByIdMutation } from "../../services/accessRole";
import { showError, showToast } from "../../constants/toasts";

const AddRole = () => {

  const navigate = useNavigate();
  const { id } = useParams()
  const [loading, setLoading] = useState<boolean>()
  const [data, setData] = useState<any>()
  const [country, setCountry] = useState<string>("")
  const [addRole, { isLoading: isSaving }] = useAddRoleMutation();
  const [getById] = useLazyGetRoleByIdQuery()
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleByIdMutation();

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) {
        setLoading(false);
        return;
      }
      const response = await getById({ id }).unwrap();
      if (response.statusCode === 200) {
        setData(response.data);
        setRoleName(response.data?.name || "");
        setCountry(response.data?.country || "");
        const permissionArray = Array.isArray(response.data?.permission) ? response.data.permission : [];
        const permissionBySidebarId: Record<number, { isView: boolean; isAdd: boolean; isEdit: boolean; isDelete: boolean }> = {};
        permissionArray.forEach((perm: any) => {
          const key = Number(perm?.sideBarId);
          if (!Number.isNaN(key)) {
            permissionBySidebarId[key] = {
              isView: !!perm?.isView,
              isAdd: !!perm?.isAdd,
              isEdit: !!perm?.isEdit,
              isDelete: !!perm?.isDelete,
            };
          }
        });
        setRows(prev => prev.map(r => {
          const p = permissionBySidebarId[r.id];
          return p ? { ...r, isView: p.isView, isAddEdit: !!(p.isAdd || p.isEdit), isDelete: p.isDelete } : r;
        }));
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };


  const sidebarModules = [
    { id: 1, name: "Dashboard" },
    { id: 2, name: "Manage User / Owner" },
    { id: 3, name: "Manage Venue / Storage" },
    { id: 4, name: "Manage Booking Requests" },
    { id: 5, name: "Loyalty Points" },
    { id: 6, name: "Manage Facilities" },
    { id: 7, name: "Discount and Coupon Management" },
    { id: 8, name: "Manage Banner" },
    { id: 9, name: "Manage News" },
    { id: 10, name: "Manage Blogs" },
    { id: 11, name: "Manage Categories" },
    { id: 12, name: "Manage FAQ" },
    { id: 13, name: "Manage Payments" },
    { id: 14, name: "Manage Host Payout" },
    { id: 15, name: "Revenue Analytics" },
    { id: 16, name: "Manage Customer Support" },
    { id: 17, name: "Notification Management" },
    { id: 18, name: "Manage Reviews" },
    { id: 19, name: "Manage CMS Pages" }
  ];

  type PermissionRow = { id: number, name: string, isView: boolean, isAddEdit: boolean, isDelete: boolean };

  const [roleName, setRoleName] = useState<string>("");
  const [rows, setRows] = useState<PermissionRow[]>(
    sidebarModules.map(m => ({ id: m.id, name: m.name, isView: false, isAddEdit: false, isDelete: false }))
  );

  const handleToggle = (rowId: number, key: keyof PermissionRow) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const toggled = { ...r, [key]: !r[key] } as PermissionRow;
      if ((key === 'isAddEdit' || key === 'isDelete') && (toggled as any)[key] === true) {
        toggled.isView = true;
      }
      return toggled;
    }));
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 180,
      field: "name",
      headerName: "Module Name",
      editable: false,
      sortable: false
    },
    {
      flex: 1,
      minWidth: 180,
      field: "isAddEdit",
      headerName: "Add / Edit",
      editable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Checkbox checked={params.row.isAddEdit} onChange={() => handleToggle(params.row.id, 'isAddEdit')} />
      )
    },
    {
      flex: 1,
      minWidth: 100,
      field: "isDelete",
      headerName: "Delete",
      editable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Checkbox checked={params.row.isDelete} onChange={() => handleToggle(params.row.id, 'isDelete')} />
      )
    },
    {
      flex: 1,
      minWidth: 180,
      field: "isView",
      headerName: "View",
      editable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Checkbox checked={params.row.isView} onChange={() => handleToggle(params.row.id, 'isView')} />
      )
    }
  ];
  let hidden = ["fullName"];

  const handleSave = async () => {
    const permission = rows.map(r => ({
      sideBarId: String(r.id),
      label: r.name,
      isView: r.isView,
      isAdd: r.isAddEdit,
      isEdit: r.isAddEdit,
      isDelete: r.isDelete,
    }));

    const body = {
      name: roleName,
      country,
      permission,
    } as any;

    try {
      if (country.length === 0) {
        showError("Country cant be empty")
        return;
      }
      if (id) {
        const res = await updateRole({ id, body }).unwrap();
        if (res?.statusCode === 200) {
          showToast("Role updated");
          navigate("/manage-subadmin/roles");
        }
      } else {
        const res = await addRole({ body }).unwrap();
        if (res?.statusCode === 200) {
          showToast("Role created");
          navigate("/manage-subadmin/roles");
        }
      }
    } catch (error) {
      // handle error silently or add toast here if available
    }
  };
  useEffect(() => {
    getDataByIdList()
  }, [id])

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? "Edit Role" : "Add Role"}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span>
          {' '} - <span onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</span>
          {' '} - <span onClick={() => navigate("/manage-subadmin/roles")}>Manage Roles</span>
          {' '} - {id ? "Edit Role" : "Add Role"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Role Name</label>
              <TextField placeholder="Enter Role Name" hiddenLabel fullWidth value={roleName} onChange={(e) => setRoleName(e.target.value)}></TextField>
            </div>
            <div className="control_group w_50">
              <label>Country</label>
              <Autocomplete
                options={(countries as Array<{ name: string; code: string; dialCode: string }>).map((c) => c.name)}
                value={country || null}
                onChange={(_, val) => setCountry(val || "")}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Country" hiddenLabel fullWidth />
                )}
                clearOnBlur
                autoHighlight
                fullWidth
                disableClearable={false}
              />
            </div>
          </div>
          <div className="sorting_table mt_20">
            <TableComponent
              columns={columns?.filter((item) => !hidden.includes(item.field))}
              rows={rows}
              getRowId={(row: any) => row.id}
            />
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" disabled={(isSaving || isUpdating) || !roleName?.trim()} onClick={handleSave}>{id ? "Update" : "Save"}</Button>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default AddRole;
